import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { getCurrentUser } from 'aws-amplify/auth';
import { uploadData, getUrl } from 'aws-amplify/storage';

const PhotoUpload = ({ onPhotoUploaded, currentPhoto }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentPhoto || null);
  const [error, setError] = useState('');

  const uploadStyles = {
    container: {
      textAlign: 'center',
      padding: '20px',
      border: '2px dashed #ddd',
      borderRadius: '10px',
      backgroundColor: '#fafafa',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    containerHover: {
      borderColor: '#5f6fff',
      backgroundColor: '#f0f2ff'
    },
    preview: {
      width: '150px',
      height: '150px',
      borderRadius: '50%',
      objectFit: 'cover',
      margin: '10px auto',
      border: '3px solid #5f6fff'
    },
    uploadIcon: {
      fontSize: '48px',
      color: '#5f6fff',
      marginBottom: '10px'
    },
    button: {
      backgroundColor: '#5f6fff',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '25px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    error: {
      color: '#dc3545',
      fontSize: '14px',
      marginTop: '10px'
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setError('');
    const file = acceptedFiles[0];
    
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to S3
    setUploading(true);
    try {
      const { userId } = await getCurrentUser();
      
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `profile-photos/${userId}/${timestamp}-${file.name}`;
      
      // Upload to S3
      const result = await uploadData({
        key: fileName,
        data: file,
        options: {
          contentType: file.type,
          accessLevel: 'guest'
        }
      }).result;

      // Get the public URL
      const { url } = await getUrl({
        key: result.key,
        options: {
          accessLevel: 'guest'
        }
      });
      
      // Callback to parent component
      if (onPhotoUploaded) {
        onPhotoUploaded(url, result.key);
      }

    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [onPhotoUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: false
  });

  const handleRemovePhoto = () => {
    setPreview(null);
    if (onPhotoUploaded) {
      onPhotoUploaded(null, null);
    }
  };

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          ...uploadStyles.container,
          ...(isDragActive ? uploadStyles.containerHover : {})
        }}
      >
        <input {...getInputProps()} />
        
        {preview ? (
          <div>
            <img src={preview} alt="Profile preview" style={uploadStyles.preview} />
            <p>Click or drag to replace photo</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePhoto();
              }}
              style={{
                ...uploadStyles.button,
                backgroundColor: '#dc3545',
                marginRight: '10px'
              }}
            >
              Remove Photo
            </button>
          </div>
        ) : (
          <div>
            <div style={uploadStyles.uploadIcon}>📷</div>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>
              {isDragActive ? 'Drop your photo here' : 'Click or drag to upload photo'}
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Supports JPG, PNG, GIF (max 5MB)
            </p>
          </div>
        )}
      </div>

      {uploading && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <div>Uploading...</div>
        </div>
      )}

      {error && (
        <div style={uploadStyles.error}>
          {error}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload; 